/**
 * Colyseus connection wrapper.
 *
 * Keeps every network detail in one place so the rest of the game deals in
 * plain callbacks and never touches the SDK directly.
 */

import { Client, Room, getStateCallbacks } from '@colyseus/sdk';
import {
  GameState,
  ClientMsg,
  ServerMsg,
  type MoveMsg,
  type WelcomeMsg,
  type CueMsg,
  type CaughtMsg,
  type ConfrontMsg,
  type ClueMsg,
  type CorrectionMsg,
  type MatchEndedMsg,
  type NoticeMsg,
  type VoiceRelayMsg,
} from '@static-pines/shared';

/**
 * Where the game server lives.
 *
 * In dev the client is served by Vite on another port, so it must be told
 * explicitly. In production the client is served by the game server itself,
 * so same-origin is correct and works behind TLS without configuration.
 */
function resolveEndpoint(): string {
  const override = import.meta.env.VITE_SERVER_URL as string | undefined;
  if (override) return override;

  if (import.meta.env.DEV) return 'ws://localhost:2567';

  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${location.host}`;
}

export interface ConnectionHandlers {
  onWelcome?: (msg: WelcomeMsg) => void;
  onCue?: (msg: CueMsg) => void;
  onCaught?: (msg: CaughtMsg) => void;
  onConfront?: (msg: ConfrontMsg) => void;
  onClue?: (msg: ClueMsg) => void;
  onCorrection?: (msg: CorrectionMsg) => void;
  onMatchEnded?: (msg: MatchEndedMsg) => void;
  onNotice?: (msg: NoticeMsg) => void;
  onVoiceSignal?: (msg: VoiceRelayMsg) => void;
  onPlayerAdd?: (sessionId: string) => void;
  onPlayerRemove?: (sessionId: string) => void;
  onLeave?: (code: number) => void;
  onError?: (message: string) => void;
}

export class Connection {
  private client: Client;
  room: Room<GameState> | null = null;
  sessionId = '';
  seed = 0;
  code = '';

  private handlers: ConnectionHandlers = {};
  private moveAccumulator = 0;

  constructor() {
    this.client = new Client(resolveEndpoint());
  }

  setHandlers(handlers: ConnectionHandlers): void {
    this.handlers = handlers;
  }

  /**
   * Join the room with the given code, creating it if nobody has yet.
   * `filterBy(['code'])` on the server makes this a private-lobby join.
   */
  async join(name: string, code?: string, difficulty?: number): Promise<void> {
    const options: Record<string, unknown> = { name };
    if (code) options.code = code.toUpperCase();
    // Only honoured when this call creates the room; joiners inherit it.
    if (difficulty !== undefined) options.difficulty = difficulty;

    const room = await this.client.joinOrCreate('forest', options);
    this.room = room as unknown as Room<GameState>;
    this.sessionId = room.sessionId;

    this.wireMessages();
    this.wireState();
  }

  private wireMessages(): void {
    const room = this.room!;

    room.onMessage(ServerMsg.Welcome, (msg: WelcomeMsg) => {
      this.seed = msg.seed;
      this.code = msg.code;
      this.handlers.onWelcome?.(msg);
    });

    room.onMessage(ServerMsg.Cue, (msg: CueMsg) => this.handlers.onCue?.(msg));
    room.onMessage(ServerMsg.Caught, (msg: CaughtMsg) => this.handlers.onCaught?.(msg));
    room.onMessage(ServerMsg.Confront, (msg: ConfrontMsg) => this.handlers.onConfront?.(msg));
    room.onMessage(ServerMsg.ClueUnlocked, (msg: ClueMsg) => this.handlers.onClue?.(msg));
    room.onMessage(ServerMsg.Correction, (msg: CorrectionMsg) =>
      this.handlers.onCorrection?.(msg),
    );
    room.onMessage(ServerMsg.MatchEnded, (msg: MatchEndedMsg) =>
      this.handlers.onMatchEnded?.(msg),
    );
    room.onMessage(ServerMsg.Notice, (msg: NoticeMsg) => this.handlers.onNotice?.(msg));
    room.onMessage(ServerMsg.VoiceSignal, (msg: VoiceRelayMsg) =>
      this.handlers.onVoiceSignal?.(msg),
    );

    room.onLeave((code) => this.handlers.onLeave?.(code));
    room.onError((code, message) => this.handlers.onError?.(message ?? `error ${code}`));
  }

  private wireState(): void {
    const room = this.room!;
    const $ = getStateCallbacks(room);

    $(room.state).players.onAdd((_player, sessionId: string) => {
      this.handlers.onPlayerAdd?.(sessionId);
    });

    $(room.state).players.onRemove((_player, sessionId: string) => {
      this.handlers.onPlayerRemove?.(sessionId);
    });
  }

  get state(): GameState | null {
    return this.room?.state ?? null;
  }

  /** Throttled position report. Called every frame; sends at CLIENT_SEND_RATE. */
  sendMove(dt: number, interval: number, msg: MoveMsg): void {
    if (!this.room) return;
    this.moveAccumulator += dt;
    if (this.moveAccumulator < interval) return;
    this.moveAccumulator = 0;
    this.room.send(ClientMsg.Move, msg);
  }

  send(type: string, payload?: unknown): void {
    this.room?.send(type, payload);
  }

  async leave(): Promise<void> {
    await this.room?.leave(true);
    this.room = null;
  }
}
